import { useEffect, useMemo, useState } from 'react';
import { cn } from '../../lib/cn';
import { api } from '../../lib/api';

/**
 * `xs22` and `sm28` are the post creator's avatar on a campaign's post card and in the All Posts table.
 * A size is a preset, never a className: `cn` only joins classes, and the stylesheet orders utilities of
 * the same property by their scale, so a caller's `h-7` beats a preset's `h-5` but loses to its `h-8`.
 */
type Size = 'xs' | 'xs22' | 'sm28' | 'sm' | 'md' | 'lg';

const sizeClasses: Record<Size, string> = {
  xs: 'h-5 w-5 text-[0.5625rem]',
  xs22: 'h-[1.375rem] w-[1.375rem] text-[0.5625rem]',
  sm28: 'h-7 w-7 text-[0.625rem]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-20 w-20 text-2xl',
};

/**
 * Deterministic background colour from the name, so a user is always the same hue. Identity colours:
 * fixed in both themes, never a theme token. Seven slots, in this order, so `hash % 7` keeps giving each
 * person the slot they always had (only slots 0, 3 and 6 changed hue, away from cyan, blue and teal).
 */
const COLORS = [
  'bg-orange-700',
  'bg-emerald-600',
  'bg-amber-600',
  'bg-fuchsia-700',
  'bg-violet-600',
  'bg-rose-600',
  'bg-lime-700',
];

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function colorFor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return COLORS[Math.abs(hash) % COLORS.length];
}

export interface AvatarProps {
  /** Display name — drives initials fallback + alt text. */
  name: string;
  size?: Size;
  /**
   * Whether to fetch the custom avatar (auth-protected) from the API. Set false
   * in dev-auth mode, or when a resolved `photoUrl` is supplied by the shared
   * UserAvatarProvider, to skip a redundant network call.
   */
  fetchCustom?: boolean;
  /**
   * Pre-resolved object URL for the avatar image (custom upload or Microsoft 365
   * Graph photo), typically from `useUserAvatar()`. Used when no locally-fetched
   * custom avatar is present.
   */
  photoUrl?: string | null;
  /**
   * Bump this to force a re-fetch of the custom avatar (e.g. after upload).
   */
  refreshKey?: number;
  /**
   * The name is printed right next to the avatar, so the picture adds nothing for a screen reader
   * and would only make it say the name twice.
   */
  decorative?: boolean;
  /**
   * `chrome`: the initials on the always-dark top bar (light disc, dark letters) instead of the
   * name's colour. Opt-in, so every other avatar keeps its own hue.
   */
  tone?: 'default' | 'chrome';
  className?: string;
}

/**
 * Renders, in priority order: the custom avatar (auth-protected blob → object
 * URL), the Microsoft 365 photo, or the user's initials in a coloured circle.
 * Object URLs are revoked on unmount / re-fetch.
 */
export function Avatar({
  name,
  size = 'md',
  fetchCustom = true,
  photoUrl = null,
  refreshKey = 0,
  decorative = false,
  tone = 'default',
  className,
}: AvatarProps) {
  const [customUrl, setCustomUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!fetchCustom) {
      setCustomUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    const controller = new AbortController();

    void api
      .fetchAvatarBlob(controller.signal)
      .then((blob) => {
        if (cancelled || !blob) return;
        objectUrl = URL.createObjectURL(blob);
        setCustomUrl(objectUrl);
      })
      .catch(() => {
        // Avatar is best-effort: fall back silently.
      });

    return () => {
      cancelled = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setCustomUrl(null);
    };
  }, [fetchCustom, refreshKey]);

  const src = customUrl ?? photoUrl ?? null;
  const dims = sizeClasses[size];
  const text = useMemo(() => initials(name), [name]);

  if (src) {
    return (
      <img
        src={src}
        alt={decorative ? '' : name}
        className={cn('shrink-0 rounded-full object-cover', dims, className)}
      />
    );
  }

  return (
    <span
      {...(decorative ? { 'aria-hidden': true } : { 'aria-label': name })}
      title={name}
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full',
        tone === 'chrome' ? 'font-bold text-chrome-bar' : 'font-semibold text-white',
        tone === 'chrome' ? 'bg-chrome-text' : colorFor(name),
        dims,
        className,
      )}
    >
      {text}
    </span>
  );
}
